import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DropResult } from "react-beautiful-dnd";

import { showErrorToast } from "@components/Common/ErrorToast";
import { RootState } from "../../../../../../redux/store";
import { ScanToContext } from "..";
import {
    ScanToMobilePreview,
    ActionsPreviewData,
    PublicitesPreviewData,
    CommandePreviewData,
    DiversPreviewData,
} from "../View";

import { PropsType, OfferItem } from "./types";
import {
    DEFAULT_SECTION_TITLES,
    SECTION_ICONS,
    LANGUAGE_CODES,
    DEFAULT_OFFERS,
    DEFAULT_OFFER_IMAGES,
} from "./constants";
import {
    CUSTOM_ICON_KEY,
    DISPLAY_TYPE_MOBILE,
    DISPLAY_KEY_MOBILE,
    DISPLAY_KEY_DESKTOP,
    SCAN_TO_DEFAULT_MAIN_COLOR,
} from "../shared/constants";
import { useMultiCustomIcons } from "../shared/useMultiCustomIcons";
import {
    Container,
    LeftSection,
    RightSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    PreviewToggleButton,
    ClosePreviewButton,
    OpenInNewWindowButton,
    PreviewOverlay,
} from "./styled";
import { SectionTitleCard } from "./SectionTitleCard";
import { SectionIconCard } from "../shared/SectionIconCard";
import { OffersTable } from "./OffersTable";
import { DeleteConfirmModal } from "./DeleteConfirmModal";

// ─── Component ───────────────────────────────────────────

export function Offres({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const {
        sharedBannerData,
        sharedActionsData,
        sharedPublicitesData,
        sharedCommandeData,
        sharedDiversData,
        sharedPaiementData,
        sharedAppelServeurData,
        setSharedOffresData,
        registerPendingFile,
        removePendingFile,
        registerDeletedImage,
        broadcastChannelRef,
    } = React.useContext(ScanToContext);

    // ─── Languages (translated labels) ───────────────────
    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // ─── Section icons (translated labels) ───────────────
    const sectionIcons = React.useMemo(
        () =>
            SECTION_ICONS.map((i) => ({
                key: i.key,
                label: t(i.labelKey),
                path: i.path,
            })),
        [t]
    );

    // ─── Default section titles and offers from constants ─
    const defaultSectionTitles = DEFAULT_SECTION_TITLES;
    const defaultOffers: OfferItem[] = DEFAULT_OFFERS;

    // ─── State ───────────────────────────────────────────
    // Cast: OffersConfig type doesn't declare every UI-state field used at
    // runtime (selectedLanguage, sectionTitles, etc.). Out of scope for ARCH-02.
    const savedOffers = useSelector(
        (state: RootState) => state.scanTo?.offers
    ) as any;
    const [offersEnabled, setOffersEnabled] = React.useState(
        savedOffers?.offersEnabled ?? true
    );
    const [titleVisible, setTitleVisible] = React.useState(
        savedOffers?.titleVisible ?? true
    );
    const [selectedLanguage, setSelectedLanguage] = React.useState(
        savedOffers?.selectedLanguage || "fr"
    );
    // Separate language state for the offers table (independent from section title)
    const [selectedOffersLang, setSelectedOffersLang] = React.useState(
        savedOffers?.selectedOffersLang || "fr"
    );
    const [selectedIcon, setSelectedIcon] = React.useState(
        savedOffers?.selectedIcon || "tag"
    );
    // Multi-icon state lifted into the shared hook so all 4 tabs
    // (Offres / Actions / MaCommande / Publicites) share one source of
    // truth — see `shared/useMultiCustomIcons.ts`.
    const {
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        setSelectedCustomIdx,
        customIconUrl,
        iconFileInputRef,
        handleIconUpload,
        removeCustomIcon,
    } = useMultiCustomIcons({
        saved: savedOffers,
        fallbackIconKey: "tag",
        setSelectedIcon,
    });
    const [displayType, setDisplayType] = React.useState(
        savedOffers?.displayType || "desktop"
    );
    const [previewVisible, setPreviewVisible] = React.useState(true);

    const [sectionTitles, setSectionTitles] = React.useState<
        Record<string, string>
    >(savedOffers?.sectionTitles || { ...defaultSectionTitles });

    const [offers, setOffers] = React.useState<{
        Mobile: OfferItem[];
        Desktop: OfferItem[];
    }>(() => {
        // fullOffers (from old previewData) or offers (from reducer) — both may exist
        const raw = savedOffers?.fullOffers || savedOffers?.offers;
        // Seed defaults whenever the saved data is missing OR empty
        if (Array.isArray(raw)) {
            return {
                Mobile: [...defaultOffers],
                Desktop: raw.length > 0 ? raw : [...defaultOffers],
            };
        }
        if (raw && raw.Mobile !== undefined) {
            return {
                Mobile: raw.Mobile.length > 0 ? raw.Mobile : [...defaultOffers],
                Desktop:
                    raw.Desktop.length > 0 ? raw.Desktop : [...defaultOffers],
            };
        }
        return { Mobile: [...defaultOffers], Desktop: [...defaultOffers] };
    });

    // Computed: current offers list based on displayType
    const displayKey =
        displayType === DISPLAY_TYPE_MOBILE
            ? DISPLAY_KEY_MOBILE
            : DISPLAY_KEY_DESKTOP;
    const currentOffers = offers[displayKey];
    const setCurrentOffers = (updated: OfferItem[]) => {
        setOffers({ ...offers, [displayKey]: updated });
    };

    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [offerToDelete, setOfferToDelete] = React.useState<number | null>(
        null
    );
    const [uploadedImages, setUploadedImages] = React.useState<
        Record<number, string>
    >({});
    const [uploadingForOfferId, setUploadingForOfferId] = React.useState<
        number | null
    >(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // bannerData and actionsData come from context (sharedBannerData, sharedActionsData)

    // ─── Computed ────────────────────────────────────────
    const activeOffers = React.useMemo(
        () =>
            [...currentOffers]
                .filter((o) => o.active)
                .sort((a, b) => a.order - b.order),
        [currentOffers]
    );

    // Fetch local default images and register them as pending files for upload
    const registerDefaultOfferImages = React.useCallback(() => {
        const defaultFilenames: Record<number, string> = {
            1: "menu_midi.jpg",
            2: "happy_hour.jpg",
            3: "dessert.jpg",
        };
        Object.entries(DEFAULT_OFFER_IMAGES).forEach(([idStr, src]) => {
            const id = Number(idStr);
            fetch(src)
                .then((res) => res.blob())
                .then((blob) => {
                    const filename = defaultFilenames[id] || `offer_${id}.jpg`;
                    const file = new File([blob], filename, {
                        type: blob.type,
                    });
                    const blobUrl = URL.createObjectURL(blob);
                    registerPendingFile(blobUrl, file);
                    setUploadedImages((prev) => ({ ...prev, [id]: blobUrl }));
                    setOffers((prev) => ({
                        Mobile: prev.Mobile.map((o) =>
                            o.id === id ? { ...o, image: filename } : o
                        ),
                        Desktop: prev.Desktop.map((o) =>
                            o.id === id ? { ...o, image: filename } : o
                        ),
                    }));
                })
                .catch(() => {});
        });
    }, [registerPendingFile]);

    // ─── Register default offer images as pending files on first mount ───
    React.useEffect(() => {
        // Only run when no blob URL is already set (avoid re-registering on HMR)
        const hasAnyBlob = Object.values(uploadedImages).some(
            (u) => typeof u === "string" && u.startsWith("blob:")
        );
        if (hasAnyBlob) return;
        registerDefaultOfferImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Share offers data via context + BroadcastChannel ───
    React.useEffect(() => {
        const previewData = {
            source: "offres",
            offersEnabled,
            titleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            offers: activeOffers.map((o) => ({
                id: o.id,
                title: o.title[selectedOffersLang] || "",
                description: o.description[selectedOffersLang] || "",
                tag: o.tag,
                url: o.url,
                imageUrl:
                    uploadedImages[o.id] ||
                    o.imageUrl ||
                    (o.image?.startsWith("http") ? o.image : ""),
            })),
            mainColor,
            selectedLanguage,
            // Full config for Redux persistence
            sectionTitles,
            selectedIcon,
            customIconUrls,
            customIconFiles: customIconUrls.map((url, i) => ({
                filename: customIconFilenames[i] || "",
                url,
            })),
            selectedCustomIdx,
            selectedOffersLang,
            fullOffers: offers,
            displayType,
        };

        // Share with other same-page tabs via context
        setSharedOffresData(previewData);

        // Send to external preview window via the centralized BroadcastChannel
        if (broadcastChannelRef.current) {
            try {
                broadcastChannelRef.current.postMessage(previewData);
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem(
            "scanToOffresPreviewData",
            JSON.stringify(previewData)
        );
    }, [
        offersEnabled,
        titleVisible,
        sectionTitles,
        selectedIcon,
        customIconUrl,
        customIconUrls,
        selectedCustomIdx,
        offers,
        displayType,
        selectedLanguage,
        selectedOffersLang,
        customIconFilenames,
        uploadedImages,
        mainColor,
        broadcastChannelRef,
        activeOffers,
        setSharedOffresData,
        sectionIcons,
    ]);

    // ─── Cleanup blob URLs on unmount ─────────────────────
    // Track the latest map in a ref so the cleanup reads it at unmount
    // without re-running on every change to `uploadedImages` — otherwise
    // each subsequent fetch (3 default offer images load one by one) would
    // revoke blob URLs still referenced by both the current state AND the
    // sessionStorage payload read by the popup window, leaving the popup
    // with dead URLs and no offer images. Mirrors Banniere / Publicites.
    const uploadedImagesRef = React.useRef(uploadedImages);
    React.useEffect(() => {
        uploadedImagesRef.current = uploadedImages;
    }, [uploadedImages]);

    React.useEffect(() => {
        return () => {
            Object.values(uploadedImagesRef.current).forEach((url) => {
                if (url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    // (Custom icon blob URL cleanup is inside useMultiCustomIcons — it
    // revokes every entry of customIconUrls on unmount, NOT on every
    // selection change, which would have killed the active blob the
    // moment the user clicked a different icon in the picker.)

    // ─── Handlers ────────────────────────────────────────

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const reordered = Array.from(currentOffers);
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);
        const updated = reordered.map((item, index) => ({
            ...item,
            order: index + 1,
        }));
        setCurrentOffers(updated);
    };

    const handleOrderChange = (offerId: number, newOrder: string) => {
        const orderNum = parseInt(newOrder, 10);
        if (isNaN(orderNum) || orderNum < 1) return;
        setCurrentOffers(
            currentOffers.map((o) =>
                o.id === offerId ? { ...o, order: orderNum } : o
            )
        );
    };

    const toggleOfferActive = (offerId: number) => {
        setCurrentOffers(
            currentOffers.map((o) =>
                o.id === offerId ? { ...o, active: !o.active } : o
            )
        );
    };

    const handleAddOffer = () => {
        const allIds = [...offers.Mobile, ...offers.Desktop].map((o) => o.id);
        const newId = Math.max(...allIds, 0) + 1;
        const emptyTexts = Object.fromEntries(
            languages.map((l) => [l.code, ""])
        );
        const newOffer: OfferItem = {
            id: newId,
            order: currentOffers.length + 1,
            image: "",
            title: { ...emptyTexts },
            description: { ...emptyTexts },
            tag: "",
            url: "",
            active: true,
        };
        setCurrentOffers([...currentOffers, newOffer]);
    };

    const handleDeleteClick = (offerId: number) => {
        setOfferToDelete(offerId);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (offerToDelete !== null) {
            const offer = currentOffers.find((o) => o.id === offerToDelete);
            if (offer?.image) registerDeletedImage(offer.image);

            if (uploadedImages[offerToDelete]) {
                removePendingFile(uploadedImages[offerToDelete]);
                URL.revokeObjectURL(uploadedImages[offerToDelete]);
                const newUploaded = { ...uploadedImages };
                delete newUploaded[offerToDelete];
                setUploadedImages(newUploaded);
            }
            const filtered = currentOffers
                .filter((o) => o.id !== offerToDelete)
                .map((o, index) => ({ ...o, order: index + 1 }));
            setCurrentOffers(filtered);
        }
        setDeleteConfirmOpen(false);
        setOfferToDelete(null);
    };

    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setOfferToDelete(null);
    };

    const handleImageClick = (offerId: number) => {
        setUploadingForOfferId(offerId);
        fileInputRef.current?.click();
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || uploadingForOfferId === null) return;

        const validTypes = ["image/png", "image/jpg", "image/jpeg"];
        if (!validTypes.includes(file.type)) {
            showErrorToast(
                t("Invalid file format. Please use PNG, JPG or JPEG.")
            );
            event.target.value = "";
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            showErrorToast(t("File size is too large. Maximum 5MB."));
            event.target.value = "";
            return;
        }

        const blobUrl = URL.createObjectURL(file);
        registerPendingFile(blobUrl, file);

        if (uploadedImages[uploadingForOfferId]) {
            removePendingFile(uploadedImages[uploadingForOfferId]);
            URL.revokeObjectURL(uploadedImages[uploadingForOfferId]);
        }

        setUploadedImages({
            ...uploadedImages,
            [uploadingForOfferId]: blobUrl,
        });
        setCurrentOffers(
            currentOffers.map((o) =>
                o.id === uploadingForOfferId
                    ? { ...o, image: file.name, imageUrl: blobUrl }
                    : o
            )
        );

        event.target.value = "";
        setUploadingForOfferId(null);
    };

    const updateOfferField = (
        offerId: number,
        field: "title" | "description" | "tag" | "url",
        value: string
    ) => {
        setCurrentOffers(
            currentOffers.map((o) => {
                if (o.id !== offerId) return o;
                if (field === "title" || field === "description") {
                    return {
                        ...o,
                        [field]: {
                            ...o[field],
                            [selectedOffersLang]: value,
                        },
                    };
                }
                return { ...o, [field]: value };
            })
        );
    };

    const resetSectionTitle = () => {
        setSectionTitles({
            ...sectionTitles,
            [selectedLanguage]: defaultSectionTitles[selectedLanguage],
        });
    };

    const resetAllSectionTitles = () => {
        setSectionTitles({ ...defaultSectionTitles });
        setTitleVisible(true);
        setSelectedLanguage("fr");
    };

    const resetAllOffers = () => {
        // Only reset offers-related data (not section titles or icons)
        Object.values(uploadedImages).forEach((url) => {
            if (url.startsWith("blob:")) {
                removePendingFile(url);
                URL.revokeObjectURL(url);
            }
        });
        setUploadedImages({});
        const freshDefaults = defaultOffers.map((o) => ({
            ...o,
            title: { ...o.title },
            description: { ...o.description },
        }));
        setOffers({ Mobile: freshDefaults, Desktop: freshDefaults });
        setSelectedOffersLang("fr");
        // Re-register default images so they appear in the table and upload on save
        registerDefaultOfferImages();
    };

    // (handleIconUpload + removeCustomIcon are provided by useMultiCustomIcons.)

    const openPreviewInNewWindow = () => {
        const previewData = {
            source: "offres",
            offersEnabled,
            titleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            offers: activeOffers.map((o) => ({
                id: o.id,
                title: o.title[selectedOffersLang] || "",
                description: o.description[selectedOffersLang] || "",
                tag: o.tag,
                url: o.url,
                imageUrl:
                    uploadedImages[o.id] ||
                    o.imageUrl ||
                    (o.image?.startsWith("http") ? o.image : ""),
            })),
            mainColor,
            selectedLanguage,
        };
        sessionStorage.setItem(
            "scanToOffresPreviewData",
            JSON.stringify(previewData)
        );
        window.open(
            "/scan-to-preview",
            "ScanToPreview",
            "width=768,height=1024"
        );
    };

    // ─── JSX ─────────────────────────────────────────────
    return (
        <Container data-testid="scan-to-offers-container">
            {/* ─── Left: Configuration ─────────────────── */}
            <LeftSection>
                {/* Toggle activation */}
                <SectionHeader>
                    <SectionTitle>{t("Offers")}</SectionTitle>
                    <ToggleContainer>
                        <ToggleLabel>{t("Disable offers")}</ToggleLabel>
                        <Toggle
                            data-testid="scan-to-offers-enabled-toggle"
                            isActive={offersEnabled}
                            mainColor={mainColor}
                            onClick={() => setOffersEnabled(!offersEnabled)}
                            aria-label={t("Disable offers")}
                        />
                    </ToggleContainer>
                </SectionHeader>

                {/* Section title (multilingual) */}
                <SectionTitleCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    defaultSectionTitles={defaultSectionTitles}
                    sectionTitles={sectionTitles}
                    onSectionTitleChange={(lang, value) =>
                        setSectionTitles({
                            ...sectionTitles,
                            [lang]: value,
                        })
                    }
                    titleVisible={titleVisible}
                    onTitleVisibleToggle={() => setTitleVisible(!titleVisible)}
                    onResetSectionTitle={resetSectionTitle}
                    onResetAllSectionTitles={resetAllSectionTitles}
                />

                {/* Section icon */}
                <SectionIconCard
                    mode="multi"
                    testIdPrefix="scan-to-offers"
                    mainColor={mainColor}
                    sectionIcons={sectionIcons}
                    selectedIcon={selectedIcon}
                    onIconSelect={setSelectedIcon}
                    customIconUrls={customIconUrls}
                    selectedCustomIdx={selectedCustomIdx}
                    onSelectCustomIcon={(idx) => {
                        setSelectedCustomIdx(idx);
                        setSelectedIcon(CUSTOM_ICON_KEY);
                    }}
                    iconFileInputRef={iconFileInputRef}
                    onIconUpload={handleIconUpload}
                    onRemoveCustomIcon={removeCustomIcon}
                />

                {/* Offers table */}
                <OffersTable
                    mainColor={mainColor}
                    languages={languages}
                    selectedLanguage={selectedOffersLang}
                    onLanguageChange={setSelectedOffersLang}
                    displayType={displayType}
                    onDisplayTypeChange={setDisplayType}
                    currentOffers={currentOffers}
                    uploadedImages={uploadedImages}
                    onDragEnd={handleDragEnd}
                    onOrderChange={handleOrderChange}
                    onImageClick={handleImageClick}
                    onUpdateOfferField={updateOfferField}
                    onToggleOfferActive={toggleOfferActive}
                    onDeleteClick={handleDeleteClick}
                    onAddOffer={handleAddOffer}
                    onResetAllOffers={resetAllOffers}
                />

                {/* Hidden file input */}
                <input
                    data-testid="scan-to-offers-file-input"
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpg,image/jpeg"
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                />
            </LeftSection>

            {/* ─── Right: Preview ───────────────────────── */}
            <PreviewOverlay
                isVisible={previewVisible}
                onClick={() => setPreviewVisible(false)}
            />
            <PreviewToggleButton
                data-testid="scan-to-offers-preview-toggle"
                mainColor={mainColor}
                onClick={() => setPreviewVisible(true)}
            >
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                </svg>
            </PreviewToggleButton>

            <RightSection isVisible={previewVisible}>
                <ClosePreviewButton
                    data-testid="scan-to-offers-close-preview"
                    onClick={() => setPreviewVisible(false)}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </ClosePreviewButton>

                <OpenInNewWindowButton
                    data-testid="scan-to-offers-open-new-window"
                    mainColor={mainColor}
                    onClick={openPreviewInNewWindow}
                    title={t("Open in new window")}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </OpenInNewWindowButton>

                <ScanToMobilePreview
                    mainColor={mainColor}
                    selectedLanguage={selectedLanguage}
                    bannerData={sharedBannerData}
                    offresData={{
                        offersEnabled,
                        titleVisible,
                        sectionTitle: sectionTitles[selectedLanguage],
                        sectionIconPath:
                            selectedIcon !== CUSTOM_ICON_KEY
                                ? sectionIcons.find(
                                      (i) => i.key === selectedIcon
                                  )?.path || sectionIcons[0].path
                                : "",
                        customIconUrl:
                            selectedIcon === CUSTOM_ICON_KEY
                                ? customIconUrl
                                : "",
                        offers: activeOffers.map((o) => ({
                            id: o.id,
                            title: o.title[selectedOffersLang] || "",
                            description:
                                o.description[selectedOffersLang] || "",
                            tag: o.tag,
                            imageUrl:
                                uploadedImages[o.id] ||
                                o.imageUrl ||
                                (o.image?.startsWith("http") ? o.image : ""),
                        })),
                    }}
                    actionsData={sharedActionsData as ActionsPreviewData | null}
                    publicitesData={
                        sharedPublicitesData as PublicitesPreviewData | null
                    }
                    commandeData={
                        sharedCommandeData as CommandePreviewData | null
                    }
                    diversData={sharedDiversData as DiversPreviewData | null}
                    paiementData={sharedPaiementData}
                    appelServeurData={sharedAppelServeurData}
                />
            </RightSection>

            {/* ─── Delete Modal ─────────────────────────── */}
            {deleteConfirmOpen && (
                <DeleteConfirmModal
                    mainColor={mainColor}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
        </Container>
    );
}
